import React from 'react';
import ModifiersUtils, { type Modifier } from 'react-day-picker';
import ReactDOM from 'react-dom';

import { MultiDatePickerBody } from './MultiDatePickerBody';
import { InputFocus, MultiDatePickerNavbar } from './MultiDatePickerNavbar';
import classnames from 'classnames';
import moment, { type Moment } from 'moment';

import type { TimeIntervalInMillis } from '@cobliteam/common-types';

import { defaultIntervalChips } from '../../PeriodSelection/PeriodSelection';
import type { DatePickerAlignment } from '../util/alignment';
import { animation } from '../util/animationStyle';
import { checkInputError, getRawValue } from '../util/inputValidator';
import {
  multiNavbarWrapper,
  pickerContainer,
  pickerPosition,
} from '../util/styles';

export enum ChipType {
  WEEK = 7,
  MONTH = 30,
}

export interface IntervalChip {
  startDate: TimeIntervalInMillis;
  endDate: TimeIntervalInMillis;
  label: string;
}

export interface Errors {
  start: boolean;
  end: boolean;
  range: boolean;
}

export interface MultiDatePickerState {
  isDayPickerOpen: boolean;
  startDate?: Moment;
  endDate?: Moment;
  month: Moment;
  hoveredDate?: Moment;
  focus: InputFocus;

  startDateError: boolean;
  endDateError: boolean;

  startInputValue: string;
  endInputValue: string;
}

export interface MultiDatePickerProps {
  handleRangeChange: (startDate: number, endDate: number) => void;
  handleChipClick?: () => void;
  disabledDays?: Modifier;
  startDate?: number;
  endDate?: number;
  intervalChips?: IntervalChip[];
  showWarningError?: boolean;
  showErrorMessage?: boolean;
  portal?: boolean;
  alignment?: DatePickerAlignment;
  dataTest?: string;
}

/*
  This class communicates the top level navbar which show the selected dates, and the bottom level
  navbar with the actual date picker
 */
export class MultiDatePicker extends React.Component<
  MultiDatePickerProps,
  MultiDatePickerState
> {
  private readonly DOM_CHANGE_DELAY = 20;

  private portalBodyElement: Element;

  private outerStartInput?: HTMLElement;

  private outerEndInput?: HTMLElement;

  private innerStartInput?: HTMLElement;

  private innerEndInput?: HTMLElement;

  private outerElementRef?: HTMLDivElement;

  private openDayPickerStartTimeout?: number;

  private openDayPickerEndTimeout?: number;

  private resetDayStartTimeout?: number;

  private resetDayEndTimeout?: number;

  constructor(props: MultiDatePickerProps) {
    super(props);

    this.portalBodyElement = document.createElement('div');
    let startDate: Moment | undefined = props.startDate
      ? moment(props.startDate)
      : undefined;
    let endDate: Moment | undefined = props.endDate
      ? moment(props.endDate)
      : undefined;

    if (startDate && endDate && startDate.isAfter(endDate)) {
      startDate = undefined;
      endDate = undefined;
    }

    this.state = {
      startDate,
      endDate,
      month: startDate || moment.utc(),
      focus: InputFocus.None,

      startDateError: false,
      endDateError: false,

      startInputValue: startDate?.format('DD/MM/YYYY') || '',
      endInputValue: endDate?.format('DD/MM/YYYY') || '',
      isDayPickerOpen: false,
    };
  }

  private openDayPicker = (focus: InputFocus) => {
    const { innerStartInput, innerEndInput } = this;

    this.setState({ isDayPickerOpen: true }, () => {
      if (innerStartInput && innerEndInput) {
        if (focus === InputFocus.StartDate) {
          /* This timeouts here are to wait for the changes in DOM, if you do it instantly the .focus() does not work. */
          this.openDayPickerStartTimeout = window.setTimeout(
            () => innerStartInput.focus(),
            this.DOM_CHANGE_DELAY
          );
        }
        if (focus === InputFocus.EndDate) {
          this.openDayPickerEndTimeout = window.setTimeout(
            () => innerEndInput.focus(),
            this.DOM_CHANGE_DELAY
          );
        }
      }
    });
  };

  private setOuterStartInput = (input?: HTMLElement) => {
    if (!input) {
      return;
    }
    this.outerStartInput = input;
  };

  private setOuterEndInput = (input?: HTMLElement) => {
    if (!input) {
      return;
    }
    this.outerEndInput = input;
  };

  private setInnerStartInput = (input?: HTMLElement) => {
    if (!input) {
      return;
    }
    this.innerStartInput = input;
  };

  private setInnerEndInput = (input?: HTMLElement) => {
    if (!input) {
      return;
    }
    this.innerEndInput = input;
  };

  private blurInputs = () => {
    if (this.innerStartInput && this.innerEndInput) {
      this.innerStartInput.blur();
      this.innerEndInput.blur();
    }
  };

  private resetOtherDay = (partialState: Partial<MultiDatePickerState>) => {
    const { focus, startDate, endDate } = this.state;

    if (focus === InputFocus.StartDate) {
      partialState.endDate = undefined;
      if (endDate?.isValid()) {
        partialState.endInputValue = '';
      }
      partialState.focus = InputFocus.EndDate;

      /* This timeout here is to wait for the changes in DOM, if you do it instantly the .focus() does not work. */
      this.resetDayEndTimeout = window.setTimeout(
        () => this.innerEndInput?.focus(),
        this.DOM_CHANGE_DELAY
      );
    } else if (focus === InputFocus.EndDate) {
      partialState.startDate = undefined;
      if (startDate?.isValid()) {
        partialState.startInputValue = '';
      }
      partialState.focus = InputFocus.StartDate;

      this.resetDayStartTimeout = window.setTimeout(
        () => this.innerStartInput?.focus(),
        this.DOM_CHANGE_DELAY
      );
    }
  };

  private isOtherDaySelected = (): boolean => {
    if (this.state.focus === InputFocus.StartDate) {
      return !!this.state.endDate;
    }
    if (this.state.focus === InputFocus.EndDate) {
      return !!this.state.startDate;
    }
    return true;
  };

  private isNewRangeValid = (day: Moment): boolean => {
    const { startDate, endDate, focus } = this.state;

    if (!this.isOtherDaySelected()) {
      return false;
    }

    if (focus === InputFocus.StartDate) {
      return day.isSameOrBefore(endDate);
    }
    if (focus === InputFocus.EndDate) {
      return day.isSameOrAfter(startDate);
    }

    return true;
  };

  private getNewDay = (day: Moment, blurFocus?: InputFocus) => {
    const formattedDay = day.format('DD/MM/YYYY');
    const newState: Partial<MultiDatePickerState> = {};
    const usedFocus = blurFocus || this.state.focus;

    if (usedFocus === InputFocus.StartDate) {
      newState.startDate = day;
      newState.startInputValue = formattedDay;
      this.resetOtherDay(newState);
      newState.focus = InputFocus.EndDate;
    }
    if (usedFocus === InputFocus.EndDate) {
      newState.endDate = day;
      newState.endInputValue = formattedDay;
    }

    return newState;
  };

  private getNewMonth = (newMonth: Moment) => {
    const month = moment(newMonth);
    const { focus } = this.state;
    if (focus === InputFocus.StartDate) {
      return month;
    }
    if (focus === InputFocus.EndDate) {
      month.subtract(1, 'month');
      return month;
    }
  };

  private handleInputChange = (inputValue: string) => {
    let newState: Partial<MultiDatePickerState> = {};

    if (this.state.focus === InputFocus.StartDate) {
      newState.startInputValue = inputValue;
    } else if (this.state.focus === InputFocus.EndDate) {
      newState.endInputValue = inputValue;
    }

    const newDate = moment(inputValue, 'DD/MM/YYYY', true);
    const utils = ModifiersUtils.ModifiersUtils;

    if (
      newDate.isValid() &&
      !utils.dayMatchesModifier(newDate.toDate(), this.props.disabledDays)
    ) {
      newState = {
        ...newState,
        ...this.getNewDay(newDate),
        month: this.getNewMonth(newDate),
      };

      if (this.isNewRangeValid(newDate)) {
        newState.focus = InputFocus.None;
        newState.isDayPickerOpen = false;
        newState.endDateError = false;
        newState.startDateError = false;
        this.blurInputs();
      } else {
        this.resetOtherDay(newState);
      }
    } else if (this.state.focus === InputFocus.StartDate) {
      newState.startDate = undefined;
      newState.startDateError = checkInputError(inputValue);
    } else if (this.state.focus === InputFocus.EndDate) {
      newState.endDate = undefined;
      newState.endDateError = checkInputError(inputValue);
    }

    this.setState(
      (currentState) => ({ ...currentState, ...newState }),
      () => {
        const { startDate, endDate } = this.state;
        if (startDate && endDate) {
          this.props.handleRangeChange(startDate.valueOf(), endDate.valueOf());
        }
      }
    );
  };

  private handleInputFocus = (focus: InputFocus) => {
    const { startInputValue, endInputValue } = this.state;
    const newState: Partial<MultiDatePickerState> = { focus };

    if (focus === InputFocus.StartDate) {
      newState.hoveredDate = this.state.startDate;
      newState.endDate = undefined;
      newState.endInputValue = '';
    }
    if (focus === InputFocus.EndDate) {
      newState.hoveredDate = this.state.endDate;
    }

    const rawStartDateLength = getRawValue(startInputValue).length;
    const rawEndDateLength = getRawValue(endInputValue).length;

    if (focus === InputFocus.EndDate) {
      if (rawStartDateLength !== 0 && rawStartDateLength !== 8) {
        newState.startDateError = true;
      }
    } else if (focus === InputFocus.StartDate) {
      if (rawEndDateLength !== 0 && rawEndDateLength !== 8) {
        newState.endDateError = true;
      }
    }

    this.setState((currentState) => ({ ...currentState, ...newState }));
  };

  private handleDayMouseEnter = (day: Moment) => {
    if (this.state.focus === InputFocus.StartDate) {
      this.setState({ hoveredDate: day, startDate: undefined });
    }
    if (this.state.focus === InputFocus.EndDate) {
      this.setState({ hoveredDate: day, endDate: undefined });
    }
  };

  private handleDayMouseLeave = () => {
    if (this.state.focus === InputFocus.StartDate) {
      const newDate = moment(this.state.startInputValue, 'DD/MM/YYYY', true);
      this.setState({ hoveredDate: undefined, startDate: newDate });
    }
    if (this.state.focus === InputFocus.EndDate) {
      const newDate = moment(this.state.endInputValue, 'DD/MM/YYYY', true);
      this.setState({ hoveredDate: undefined, endDate: newDate });
    }
  };

  private handleDayMouseDown = (date: Moment) => {
    let newState: Partial<MultiDatePickerState> = this.getNewDay(date);

    if (!this.isNewRangeValid(date)) {
      this.resetOtherDay(newState);
      this.setState((currentState) => ({ ...currentState, ...newState }));
      return;
    }

    newState = {
      ...newState,
      startDateError: false,
      endDateError: false,
    };

    /* The dates have been defined */
    newState.focus = InputFocus.None;
    if (newState.endDate) newState.isDayPickerOpen = false;
    this.setState(
      (currentState) => ({ ...currentState, ...newState }),
      () => {
        const { startDate, endDate } = this.state;
        if (startDate && endDate) {
          this.props.handleRangeChange(startDate.valueOf(), endDate.valueOf());
        }
      }
    );
  };

  private handleChipClick = (chip: IntervalChip) => {
    if (this.props.handleChipClick) {
      this.props.handleChipClick();
    }
    const start = chip.startDate;
    const end = chip.endDate;

    const month = moment(end).subtract(1, 'month');

    const newState: Partial<MultiDatePickerState> = {
      startInputValue: moment(start).format('DD/MM/YYYY'),
      endInputValue: moment(end).format('DD/MM/YYYY'),
      startDate: moment(start),
      endDate: moment(end),
      focus: InputFocus.None,
      isDayPickerOpen: false,
      month,
    };

    this.blurInputs();

    this.setState(
      (currentState) => ({ ...currentState, ...newState }),
      () => {
        this.props.handleRangeChange(start, end);
      }
    );
  };

  private hasDateUpdate(currentDate?: Moment, previouDate?: Moment) {
    return currentDate && (!previouDate || !previouDate.isSame(currentDate));
  }

  public componentDidMount = () => {
    document.body.appendChild(this.portalBodyElement);
    document.addEventListener('mousedown', this.handleOutsideClick, false);
  };

  public componentWillUnMount = () => {
    document.body.removeChild(this.portalBodyElement);
    document.removeEventListener('mousedown', this.handleOutsideClick, false);
    clearInterval(this.openDayPickerStartTimeout);
    clearInterval(this.openDayPickerEndTimeout);
    clearInterval(this.resetDayStartTimeout);
    clearInterval(this.resetDayEndTimeout);
  };

  private handleOutsideClick = (event: MouseEvent) => {
    const node = event.target as Node;

    const newState: Partial<MultiDatePickerState> = {};

    // Checks here if the click comes from inside the very outer div and if not it means that we must close the picker
    // Does not work with portal props
    const isClickInsidePicker = this.outerElementRef?.contains(node);
    if (!this.props.portal) {
      if (isClickInsidePicker) {
        return;
      }
      newState.isDayPickerOpen = false;
    }

    // Catches portal props usage to identify outside click
    const isClickInsidePickerPortal = this.portalBodyElement.contains(node);
    if (this.props.portal) {
      if (isClickInsidePickerPortal) {
        return;
      }
      newState.isDayPickerOpen = false;
    }

    // Show error on close
    if (this.state.startInputValue.length > 0 && !this.state.startDate) {
      this.setState({ startDateError: true });
    }
    if (this.state.endInputValue.length > 0 && !this.state.endDate) {
      this.setState({ endDateError: true });
    }

    this.setState((currentState) => ({ ...currentState, ...newState }));
  };

  private handleMonthChange = (month: Moment) => {
    this.setState({ month });
  };

  public componentDidUpdate = (prevProps: MultiDatePickerProps) => {
    const { focus } = this.state;

    const { innerStartInput, innerEndInput } = this;

    const prevStartDate = prevProps.startDate
      ? moment(prevProps.startDate)
      : undefined;
    const prevEndDate = prevProps.endDate
      ? moment(prevProps.endDate)
      : undefined;

    const curStartDate = this.props.startDate
      ? moment(this.props.startDate)
      : undefined;
    const curEndDate = this.props.endDate
      ? moment(this.props.endDate)
      : undefined;

    if (
      this.hasDateUpdate(curStartDate, prevStartDate) ||
      this.hasDateUpdate(curEndDate, prevEndDate)
    ) {
      this.setState({
        startDate: curStartDate,
        startInputValue: curStartDate ? curStartDate.format('DD/MM/YYYY') : '',
        endDate: curEndDate,
        endInputValue: curEndDate ? curEndDate.format('DD/MM/YYYY') : '',
      });
    }

    if (
      focus === undefined ||
      focus === InputFocus.None ||
      !innerStartInput ||
      !innerEndInput
    ) {
      return;
    }

    if (focus === InputFocus.StartDate) {
      innerStartInput.focus();
    } else if (focus === InputFocus.EndDate) {
      innerEndInput.focus();
    }
  };

  private renderMultiDatePickerBody = (
    errors: Errors,
    animationCss: ReturnType<typeof animation>
  ) => {
    const {
      isDayPickerOpen,
      startInputValue,
      endInputValue,
      startDate,
      endDate,
      focus,
      hoveredDate,
      month,
    } = this.state;

    const { hideCalendar, calendar, multiCalendar } = animationCss;
    const renderPickerBody = (styles?: string) => (
      <div>
        <MultiDatePickerBody
          className={classnames(styles, calendar, multiCalendar, {
            [hideCalendar]: !isDayPickerOpen,
          })}
          handleInputChange={this.handleInputChange}
          handleInputFocus={this.handleInputFocus}
          handleDayMouseEnter={this.handleDayMouseEnter}
          handleDayMouseLeave={this.handleDayMouseLeave}
          handleDayMouseDown={this.handleDayMouseDown}
          handleChipClick={this.handleChipClick}
          startDate={startDate}
          endDate={endDate}
          startDateInputValue={startInputValue}
          endDateInputValue={endInputValue}
          errors={errors}
          showErrorMessage={this.props.showErrorMessage}
          focus={focus}
          setStartInput={this.setInnerStartInput}
          setEndInput={this.setInnerEndInput}
          hoveredDate={hoveredDate}
          month={month}
          handleMonthChange={this.handleMonthChange}
          disabledDays={this.props.disabledDays}
          intervalChips={
            this.props.intervalChips
              ? this.props.intervalChips
              : defaultIntervalChips
          }
        />
      </div>
    );

    if (this.props.portal && this.outerElementRef) {
      const { top, left } = this.outerElementRef.getBoundingClientRect();
      const right = this.outerElementRef.offsetLeft;
      const portalStyles = pickerPosition(
        top,
        right,
        left,
        this.props.alignment
      );
      return ReactDOM.createPortal(
        renderPickerBody(portalStyles),
        this.portalBodyElement
      );
    }
    return renderPickerBody();
  };

  public render() {
    const {
      isDayPickerOpen,
      startInputValue,
      endInputValue,
      startDateError,
      endDateError,
    } = this.state;

    const errors: Errors = {
      start: startDateError,
      end: endDateError,
      range: Boolean(this.props.showWarningError),
    };
    const animationCss = animation(this.props.alignment);
    return (
      <div
        className={pickerContainer(isDayPickerOpen, errors.start)}
        ref={(ref: HTMLDivElement) => (this.outerElementRef = ref)}
      >
        <div
          className={classnames(animationCss.navbar, multiNavbarWrapper, {
            [animationCss.hideNavbar]: isDayPickerOpen,
          })}
        >
          <MultiDatePickerNavbar
            handleInputFocus={this.openDayPicker}
            handleInputChange={() => {}}
            errors={errors}
            showErrorMessage={this.props.showErrorMessage}
            startDateInputValue={startInputValue}
            endDateInputValue={endInputValue}
            setStartInput={this.setOuterStartInput}
            setEndInput={this.setOuterEndInput}
            isDayPickerOpen={!isDayPickerOpen}
            focusable={false}
            dataTest={this.props.dataTest}
          />
        </div>
        {this.renderMultiDatePickerBody(errors, animationCss)}
      </div>
    );
  }
}
